import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

/**
 * Compares fractional widths: scrollWidth/clientWidth round to integers, so a
 * sub-pixel overflow that still draws the ellipsis would measure as fitting.
 */
export function isContentClipped(el: HTMLElement): boolean {
  const range = document.createRange();
  range.selectNodeContents(el);
  // jsdom has no Range layout; fall back to the rounded widths there.
  if (typeof range.getBoundingClientRect !== "function") {
    return el.scrollWidth > el.clientWidth;
  }
  return range.getBoundingClientRect().width > el.getBoundingClientRect().width;
}

/**
 * Tracks whether a nowrap value is cut off by its ellipsis. A click, or Enter or
 * Space, on a cut-off value is swallowed and reported through `onOpen`; a value
 * that fits lets the event through to whatever the value does on its own.
 */
export function useExpandWhenClipped({
  enabled,
  text,
  onOpen,
}: {
  enabled: boolean;
  text: string | undefined;
  onOpen: (event: React.SyntheticEvent<HTMLElement>) => void;
}) {
  const valueRef = useRef<HTMLDivElement>(null);
  const [isClipped, setIsClipped] = useState(false);

  const measure = useCallback(() => {
    const el = valueRef.current;
    const clipped = el !== null && isContentClipped(el);
    setIsClipped(clipped);
    return clipped;
  }, []);

  // Re-measured on resize and whenever the text changes, since a new value in
  // the same-sized box doesn't fire the observer.
  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    measure();
    const el = valueRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, text, measure]);

  // Measured again at click time so a stale state can't open or swallow wrongly.
  const onClickCapture = (event: React.MouseEvent<HTMLElement>) => {
    if (enabled && measure()) {
      event.stopPropagation();
      onOpen(event);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if ((event.key === "Enter" || event.key === " ") && enabled && measure()) {
      // Contained, so Space in a grid cell doesn't also page the grid.
      event.stopPropagation();
      event.preventDefault();
      onOpen(event);
    }
  };

  return { valueRef, isClipped: enabled && isClipped, onClickCapture, onKeyDown };
}
